interface AppointmentBookingBooxiDataset extends DOMStringMap {
  apiKey: string;
  language: string;
  booxiSourceUrl: string;
}

/** Booxi-specific API */
interface BooxiConfig {
  apiKey: string;
  language: string;
  bookingFlow?: string;
  locationCategory?: string;
}

declare global {
  interface Window {
    BookNow?: {
      open?: (options: BooxiConfig) => void;
    };
    ElcAppointmentBooking?: {
      openBooxi?: (payload?: BooxiConfig) => void;
    };
  }
}

export const AppointmentBookingBooxi = {
  linkClickHandlers: new Map(),
  hashChangeHandler: null,

  async mounted() {
    const dataSet = this.el.dataset as AppointmentBookingBooxiDataset;
    if (!dataSet.apiKey || !dataSet.language || !dataSet.booxiSourceUrl) {
      console.error('[AppointmentBookingBooxi] Missing required configuration:', {
        apiKey: dataSet.apiKey,
        language: dataSet.language,
        booxiSourceUrl: dataSet.booxiSourceUrl
      });
      return;
    }
    const booxiConfig: BooxiConfig = {
      apiKey: dataSet.apiKey,
      language: dataSet.language
    };
    try {
      await this.loadBooxiScript(dataSet.booxiSourceUrl);
      this.initializeBookLinkListeners(booxiConfig);
      // Exposing global function to open Booxi, so it can be triggered from iframes via iframe-controller
      window.ElcAppointmentBooking = {
        openBooxi: (payload) => this.openBooxiWindow({ ...booxiConfig, ...payload })
      };
    } catch (error) {
      console.error('[AppointmentBookingBooxi] Error loading Booxi script:', error);
    }
  },
  loadBooxiScript(booxiSourceUrl: string) {
    return new Promise<void>((resolve, reject) => {
      const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
        (script) => script.src === booxiSourceUrl
      );

      if (scriptLoaded) {
        return resolve();
      }

      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = reject;
      s.src = booxiSourceUrl;
      document.body.appendChild(s);
    });
  },
  initializeBookLinkListeners(config: BooxiConfig) {
    // Intercept all anchor tags whose href contains #booknow (e.g., "spa-services#booknow")
    const allLinks = document.querySelectorAll('a');
    Array.from(allLinks)
      .filter((link) => {
        const href = link.getAttribute('href');
        return href && /#booknow/i.test(href);
      })
      .forEach((el) => {
        const handler = (e: Event) => {
          e.preventDefault();
          this.openBooxiWindow(config);
        };

        el.addEventListener('click', handler);
        this.linkClickHandlers.set(el, handler);
      });

    // Fallback: listen for hashchange in url
    this.hashChangeHandler = () => {
      if (location.hash.includes('#booknow')) {
        this.openBooxiWindow(config);
        this.removeHashInUrl();
      }
    };
    window.addEventListener('hashchange', this.hashChangeHandler);
  },
  openBooxiWindow(config: BooxiConfig) {
    if (typeof window.BookNow?.open === 'function') {
      window.BookNow.open({
        bookingFlow: '', // 'locations' will force into location selection first
        locationCategory: 'required',
        ...config
      });
    }
  },
  removeHashInUrl() {
    const newHash = location.hash.replace(/#booknow[^#]*/gi, '');
    history.replaceState(null, '', `${location.pathname}${location.search}${newHash}`);
  },
  destroyed() {
    this.linkClickHandlers.forEach((handler, link) => {
      link.removeEventListener('click', handler);
    });
    this.linkClickHandlers.clear();

    if (this.hashChangeHandler) {
      window.removeEventListener('hashchange', this.hashChangeHandler);
      this.hashChangeHandler = null;
    }
  }
};
